"use client";

import {
  BodyLong,
  Box,
  Button,
  Checkbox,
  ErrorSummary,
  Heading,
  InlineMessage,
} from "@navikt/ds-react";
import { type RefObject, useRef, useState } from "react";
import { usePlanDelingContext } from "@/components/FerdigstiltPlanSider/AktivPlanSide/PlanDelingContext.tsx";
import type { FetchResultError } from "@/server/tokenXFetch/FetchResult";
import { FetchErrorAlert } from "@/ui/FetchErrorAlert";
import { getFormattedDateAndTimeString } from "@/ui-helpers/dateAndTime.ts";

interface Props {
  planId: string;
  /**
   * Styres av det eksisterende flaggskip-oppsettet. Når arbeidsgiveren er med i
   * Nav-tiltaksgruppen, vises tekstene fra tiltakspakken. Alle andre beholder
   * dagens tekster.
   */
  erITiltaksgruppe?: boolean;
}

const TEKSTER = {
  standard: {
    overskrift: "Hvem vil du sende planen til",
    beskrivelse:
      "Du skal sende oppfølgingsplanen til fastlegen innen den ansatte har vært helt eller delvis borte fra jobben i 4 uker. I tillegg kan du sende planen til Nav når du selv ønsker, når Nav ber om den, eller senest én uke før et dialogmøte med Nav.",
    knapp: "Send planen",
    fastlegeHjelpetekst: undefined,
    veilederHjelpetekst: undefined,
  },
  tiltaksgruppe: {
    overskrift: "Del oppfølgingsplanen med fastlege og Nav",
    beskrivelse:
      "Det er et krav om å dele oppfølgingsplanen med fastlegen innen de første 4 ukene av sykefraværsperioden. I tillegg kan du dele den med Nav når du selv ønsker eller senest én uke før dialogmøte med Nav (Nav kan også be om å få den tilsendt).",
    knapp: "Del planen",
    fastlegeHjelpetekst:
      "Hensikten er at fastlegen har innsikt i arbeidssituasjonen før neste konsultasjon med den som er sykmeldt.",
    veilederHjelpetekst:
      "Hensikten er at Nav har riktig informasjon for å bidra på best mulig måte i sykefraværet.",
  },
} as const;

interface RecipientSectionProps {
  id: string;
  name: string;
  label: string;
  /** Hjelpetekst under avhukingsboksen. Aksel kobler den til checkboxen med aria-describedby. */
  hjelpetekst?: string;
  sentTimestamp: string | null;
  error: FetchResultError | null;
  errorMessage: string;
  checkboxRef: RefObject<HTMLInputElement | null>;
}

function SentMessage({
  timestamp,
  recipient,
}: {
  timestamp: string;
  recipient: string;
}) {
  return (
    <InlineMessage status="success" role="status" className="py-3">
      Sendt til {recipient}
      {timestamp && ` ${getFormattedDateAndTimeString(timestamp)}`}.
    </InlineMessage>
  );
}

function RecipientCheckbox({
  id,
  name,
  label,
  hjelpetekst,
  error,
  errorMessage,
  checkboxRef,
}: RecipientSectionProps) {
  return (
    <div>
      <Checkbox id={id} ref={checkboxRef} name={name} description={hjelpetekst}>
        {label}
      </Checkbox>
      {error && (
        <FetchErrorAlert
          error={error}
          fallbackMessage={errorMessage}
          className="mt-2"
        />
      )}
    </div>
  );
}

function DelAktivPlanMedLegeEllerNav({
  planId,
  erITiltaksgruppe = false,
}: Props) {
  const {
    deltMedLegeTidspunkt,
    deltMedVeilederTidspunkt,
    delMedLegeAction,
    delMedVeilederAction,
    isPendingDelMedLege,
    isPendingDelMedVeileder,
    errorDelMedLege,
    errorDelMedVeileder,
  } = usePlanDelingContext();

  const fastlegeCheckboxRef = useRef<HTMLInputElement>(null);
  const veilederCheckboxRef = useRef<HTMLInputElement>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const isSending = isPendingDelMedLege || isPendingDelMedVeileder;
  const sentToFastlege = Boolean(deltMedLegeTidspunkt);
  const sentToVeileder = Boolean(deltMedVeilederTidspunkt);
  const hasUnsentRecipients = !sentToFastlege || !sentToVeileder;
  const tekster = erITiltaksgruppe ? TEKSTER.tiltaksgruppe : TEKSTER.standard;

  const handleSubmit = (formData: FormData) => {
    const sendToFastlege = formData.get("sendToFastlege") === "on";
    const sendToVeileder = formData.get("sendToVeileder") === "on";

    if (!sendToFastlege && !sendToVeileder) {
      setValidationError(
        "Du må velge minst ett alternativ for å sende planen.",
      );
      return;
    }

    setValidationError(null);

    if (sendToFastlege) delMedLegeAction({ planId });
    if (sendToVeileder) delMedVeilederAction({ planId });
  };

  // Når planen er sendt til både fastlege og Nav-veileder, er delingsboksen
  // ikke lenger relevant. Delingsstatusen vises da i taggene øverst på siden.
  if (!hasUnsentRecipients) {
    return null;
  }

  return (
    <Box
      background="info-soft"
      padding="space-24"
      borderRadius="12"
      borderColor="neutral-subtle"
      borderWidth="1"
    >
      <Heading level="2" size="medium" spacing>
        {tekster.overskrift}
      </Heading>
      <BodyLong>{tekster.beskrivelse}</BodyLong>

      <form action={handleSubmit}>
        {sentToFastlege ? (
          // biome-ignore lint/style/noNonNullAssertion: sentToFastlege er true, så deltMedLegeTidspunkt kan ikke være null
          <SentMessage timestamp={deltMedLegeTidspunkt!} recipient="fastlege" />
        ) : (
          <RecipientCheckbox
            id="fastlege-checkbox"
            name="sendToFastlege"
            label="Fastlegen til den ansatte"
            hjelpetekst={tekster.fastlegeHjelpetekst}
            sentTimestamp={deltMedLegeTidspunkt}
            error={errorDelMedLege}
            errorMessage="Kunne ikke sende planen til fastlege. Prøv igjen."
            checkboxRef={fastlegeCheckboxRef}
          />
        )}

        {sentToVeileder ? (
          <SentMessage
            // biome-ignore lint/style/noNonNullAssertion: sentToVeileder er true, så deltMedVeilederTidspunkt kan ikke være null
            timestamp={deltMedVeilederTidspunkt!}
            recipient="Nav-veileder"
          />
        ) : (
          <RecipientCheckbox
            id="veileder-checkbox"
            name="sendToVeileder"
            label="Nav-veilederen"
            hjelpetekst={tekster.veilederHjelpetekst}
            sentTimestamp={deltMedVeilederTidspunkt}
            error={errorDelMedVeileder}
            errorMessage="Kunne ikke sende planen til Nav-veileder. Prøv igjen."
            checkboxRef={veilederCheckboxRef}
          />
        )}

        {validationError && (
          <ErrorSummary heading="Feil ved sending av plan" className="my-4">
            <ErrorSummary.Item
              href={
                sentToFastlege ? "#veileder-checkbox" : "#fastlege-checkbox"
              }
            >
              {validationError}
            </ErrorSummary.Item>
          </ErrorSummary>
        )}

        {hasUnsentRecipients && (
          <Button type="submit" loading={isSending} className="mt-4">
            {tekster.knapp}
          </Button>
        )}
      </form>
    </Box>
  );
}

export default DelAktivPlanMedLegeEllerNav;
